import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface ConfirmationDialogProps {
  open: boolean;
  agent: string;
  changes: {
    agent: string;
    added: string[];
    removed: string[];
  };
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmationDialog({
  open,
  agent,
  changes,
  onConfirm,
  onCancel,
}: ConfirmationDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setIsSubmitting(false);
      setConfirmText('');
    }
  };

  const handleCancel = () => {
    setConfirmText('');
    onCancel();
  };

  const totalChanges = changes.added.length + changes.removed.length;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">
            Confirm Permission Changes
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          You are about to modify permissions for <strong>{agent}</strong>.
          These changes will affect production immediately after email confirmation.
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Summary: {totalChanges} change{totalChanges !== 1 ? 's' : ''}
          </Typography>

          {changes.added.length > 0 && (
            <Chip
              label={`${changes.added.length} services added`}
              color="success"
              size="small"
              sx={{ mr: 1 }}
            />
          )}

          {changes.removed.length > 0 && (
            <Chip
              label={`${changes.removed.length} services removed`}
              color="error"
              size="small"
            />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {changes.added.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'success.main',
                mb: 1
              }}
            >
              <AddIcon /> Granting access to:
            </Typography>
            <List dense>
              {changes.added.map(service => (
                <ListItem key={service}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={service}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {changes.removed.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'error.main',
                mb: 1
              }}
            >
              <RemoveIcon /> Revoking access from:
            </Typography>
            <List dense>
              {changes.removed.map(service => (
                <ListItem key={service}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CloseIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={service}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            To confirm these changes, type the agent name below:
          </Typography>
          <TextField
            fullWidth
            label={`Type "${agent}" to confirm`}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            error={confirmText !== '' && confirmText !== agent}
            helperText={
              confirmText !== '' && confirmText !== agent
                ? `Must match exactly: ${agent}`
                : 'This helps prevent accidental changes'
            }
            autoComplete="off"
            sx={{ mt: 1 }}
          />
        </Box>

        <Alert severity="info" sx={{ mt: 3 }}>
          After confirming here, you'll receive an email to finalize these changes.
          The email link expires in 15 minutes.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="warning"
          disabled={confirmText !== agent || isSubmitting}
          sx={{ minWidth: 180 }}
        >
          {isSubmitting ? 'Sending Email...' : 'Send Confirmation Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
